const SURFACE = 'portal';

const TEXT = {
  en: {
    scheduledTitle: 'Scheduled maintenance',
    unscheduledTitle: 'Urgent maintenance notice',
    upcomingDefault: 'PATH will be unavailable for maintenance soon.',
    upcomingWithCountdown: countdown => `PATH will be unavailable for maintenance in ${countdown}.`,
    activeDefault: 'PATH is currently undergoing maintenance.',
    activeOverdue: 'PATH is still undergoing maintenance and is taking longer than expected.',
    expectedDowntime: duration => `Expected downtime: ${duration}.`,
  },
  fr: {
    scheduledTitle: 'Maintenance planifiee',
    unscheduledTitle: 'Avis urgent de maintenance',
    upcomingDefault: 'PATH sera bientot indisponible pour une maintenance.',
    upcomingWithCountdown: countdown => `PATH sera indisponible pour une maintenance dans ${countdown}.`,
    activeDefault: 'PATH fait actuellement l objet d une maintenance.',
    activeOverdue: 'La maintenance de PATH se poursuit et prend plus de temps que prevu.',
    expectedDowntime: duration => `Duree prevue de l interruption : ${duration}.`,
  },
};

export function isMaintenanceAnnouncementVisible(announcement) {
  if (!announcement || announcement.enabled !== true) return false;
  const surfaces = Array.isArray(announcement.surfaces) ? announcement.surfaces : [];
  return surfaces.includes(SURFACE);
}

export function getMaintenanceAnnouncementPhase(announcement, now = new Date()) {
  if (!isMaintenanceAnnouncementVisible(announcement)) return 'hidden';
  const startsAt = announcement?.startsAt ? Date.parse(announcement.startsAt) : null;
  if (Number.isFinite(startsAt) && startsAt > now.getTime()) {
    return 'scheduled';
  }
  return 'active';
}

function formatDuration(minutes, locale = 'en') {
  const numeric = Number(minutes);
  if (!Number.isFinite(numeric) || numeric <= 0) return '';
  const isFrench = locale === 'fr';
  if (numeric < 60) {
    return isFrench
      ? `${numeric} minute${numeric === 1 ? '' : 's'}`
      : `${numeric} minute${numeric === 1 ? '' : 's'}`;
  }
  const hours = Math.floor(numeric / 60);
  const remainder = numeric % 60;
  if (!remainder) {
    return isFrench
      ? `${hours} heure${hours === 1 ? '' : 's'}`
      : `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return isFrench
    ? `${hours} heure${hours === 1 ? '' : 's'} ${remainder} minute${remainder === 1 ? '' : 's'}`
    : `${hours} hour${hours === 1 ? '' : 's'} ${remainder} minute${remainder === 1 ? '' : 's'}`;
}

function formatCountdown(target, now = new Date(), locale = 'en') {
  const diffSeconds = Math.max(0, Math.round((Date.parse(target) - now.getTime()) / 1000));
  const isFrench = locale === 'fr';
  if (diffSeconds < 60) {
    return isFrench
      ? `${diffSeconds} seconde${diffSeconds === 1 ? '' : 's'}`
      : `${diffSeconds} second${diffSeconds === 1 ? '' : 's'}`;
  }
  const minutes = Math.round(diffSeconds / 60);
  if (minutes < 60) {
    return isFrench
      ? `${minutes} minute${minutes === 1 ? '' : 's'}`
      : `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return formatDuration(Math.round(diffSeconds / 60), locale);
}

export function buildMaintenanceAnnouncementDisplay(announcement, { now = new Date(), locale = 'en' } = {}) {
  const copy = TEXT[locale] || TEXT.en;
  const phase = getMaintenanceAnnouncementPhase(announcement, now);
  if (phase === 'hidden') return null;

  const title = announcement?.title?.[locale]
    || (announcement?.status === 'unscheduled' ? copy.unscheduledTitle : copy.scheduledTitle);
  const customBody = announcement?.body?.[locale] || '';
  const durationText = formatDuration(announcement?.expectedDurationMinutes, locale);
  const expectedEndAt = announcement?.expectedEndAt ? Date.parse(announcement.expectedEndAt) : null;
  const isPastExpectedEnd = Number.isFinite(expectedEndAt) && expectedEndAt < now.getTime();

  let heading = customBody;
  if (!heading) {
    if (phase === 'scheduled') {
      heading = announcement?.startsAt
        ? copy.upcomingWithCountdown(formatCountdown(announcement.startsAt, now, locale))
        : copy.upcomingDefault;
    } else {
      heading = isPastExpectedEnd ? copy.activeOverdue : copy.activeDefault;
    }
  }

  const bodyParts = [];
  if (durationText) {
    bodyParts.push(copy.expectedDowntime(durationText));
  }

  return {
    title,
    heading,
    body: bodyParts.join(' '),
  };
}
